import React from 'react';
import { LegalPageLayout, LegalSection, LegalCallout } from '../components/LegalPageLayout';

const TOC = [
  { id: 'overview', label: 'Overview' },
  { id: 'prohibited', label: 'Prohibited Activities' },
  { id: 'messaging', label: 'Messaging Rules (SMS & Email)' },
  { id: 'content', label: 'Content Standards' },
  { id: 'security', label: 'Security & Platform Integrity' },
  { id: 'licensing', label: 'Licensed & Regulated Activities' },
  { id: 'enforcement', label: 'Enforcement' },
  { id: 'reporting', label: 'Reporting Abuse' },
];

const AcceptableUsePolicy = () => (
  <LegalPageLayout title="Acceptable Use Policy" lastUpdated="August 5, 2026" toc={TOC}>
    <LegalSection id="overview" number={1} title="Overview">
      <p>
        This Acceptable Use Policy (&ldquo;AUP&rdquo;) sets the ground rules for using BookingFlows — the
        dashboard, booking pages, widgets, APIs, and messaging tools. It forms part of our{' '}
        <a href="/terms">Terms of Service</a>. In short: use the platform to run a legitimate business,
        respect your customers, and don&apos;t abuse the system or the people on it.
      </p>
    </LegalSection>

    <LegalSection id="prohibited" number={2} title="Prohibited Activities">
      <p>You may not use BookingFlows to:</p>
      <ul>
        <li>Conduct, promote, or facilitate illegal activity of any kind;</li>
        <li>Sell services you are not legally permitted or licensed to provide;</li>
        <li>Infringe intellectual property rights — including using photos, logos, or content you don&apos;t have rights to (see our <a href="/dmca">DMCA Policy</a>);</li>
        <li>Defraud, mislead, or deceive customers — including fake availability, hidden fees, or misrepresenting your identity or business;</li>
        <li>Collect payments for goods or services you do not intend to deliver;</li>
        <li>Harvest or scrape data about other Operators or their customers;</li>
        <li>Resell, sublicense, or white-label the platform without written authorization;</li>
        <li>Create accounts by automated means or under false identities.</li>
      </ul>
    </LegalSection>

    <LegalSection id="messaging" number={3} title="Messaging Rules (SMS & Email)">
      <p>
        BookingFlows includes SMS and email tools for booking confirmations, reminders, and marketing.
        When using them you must:
      </p>
      <ul>
        <li>Send marketing messages only to recipients who have given the required consent (TCPA, CAN-SPAM, GDPR, CASL, and equivalents apply);</li>
        <li>Honor opt-outs immediately — the platform processes STOP/unsubscribe automatically, and you must not attempt to circumvent it;</li>
        <li>Not send spam, chain messages, or content unrelated to your business;</li>
        <li>Accurately identify yourself as the sender in all communications.</li>
      </ul>
      <LegalCallout>
        <p>
          Carrier violations put the whole platform&apos;s deliverability at risk, so messaging abuse leads
          to immediate suspension of messaging features — before, not after, a warning where necessary.
        </p>
      </LegalCallout>
    </LegalSection>

    <LegalSection id="content" number={4} title="Content Standards">
      <p>Content you upload (service descriptions, images, blog content, waiver text) must not:</p>
      <ul>
        <li>Be unlawful, defamatory, harassing, or hateful;</li>
        <li>Contain sexually explicit material or content harmful to minors;</li>
        <li>Contain malware, hidden scripts, or code intended to harm visitors;</li>
        <li>Impersonate another person or business.</li>
      </ul>
    </LegalSection>

    <LegalSection id="security" number={5} title="Security & Platform Integrity">
      <p>You may not:</p>
      <ul>
        <li>Probe, scan, or test the vulnerability of the platform except under our <a href="/vulnerability-disclosure">Vulnerability Disclosure Policy</a>;</li>
        <li>Attempt to access data belonging to another organization or user;</li>
        <li>Interfere with or disrupt the platform — including overloading APIs beyond documented rate limits, or bypassing authentication or metering;</li>
        <li>Share account credentials or API keys with unauthorized parties.</li>
      </ul>
    </LegalSection>

    <LegalSection id="licensing" number={6} title="Licensed & Regulated Activities">
      <p>
        Many activities booked through the platform — charters, boat rentals, guided tours, diving — are
        regulated. Operators are solely responsible for holding all licenses, permits, certifications,
        and insurance required for the services they sell, and for complying with safety regulations
        applicable to their operation. BookingFlows is booking software, not a licensing authority, and
        does not verify regulatory compliance.
      </p>
    </LegalSection>

    <LegalSection id="enforcement" number={7} title="Enforcement">
      <p>
        We may investigate suspected violations and take action proportional to the issue — including
        removing content, suspending specific features (such as messaging), suspending or terminating
        accounts, and notifying law enforcement where appropriate. Where practical, we&apos;ll warn you and
        give you a chance to fix the problem first; for severe abuse (fraud, security attacks, carrier
        violations) we may act immediately.
      </p>
    </LegalSection>

    <LegalSection id="reporting" number={8} title="Reporting Abuse">
      <p>
        To report a violation of this policy — abusive content, spam from an Operator, suspected fraud —
        contact us through our <a href="/support#tickets">Support Center</a>. For security
        vulnerabilities, use the <a href="/vulnerability-disclosure">Vulnerability Disclosure Policy</a>.
      </p>
    </LegalSection>
  </LegalPageLayout>
);

export default AcceptableUsePolicy;
